/**
 * parts.tsx — byggeklosser til inFiles-illustrasjonene (isometrisk SVG)
 *
 * DocStack: stabel med ark (original = grå, modifisert = blå topplag med
 * tekstlinjer). Chip: ECU-brikke med ben rundt en blå kjerne. Begge bruker
 * Box fra components/product/iso.tsx. Kun blå og grå — ingen lilla/blågrønn.
 */

import { Box, iso as heroIso } from '@/components/product/iso';

type Project = (x: number, y: number, z: number) => string;

const PAPER: [string, string, string] = ['#ffffff', '#e0e0e0', '#c6c6c6'];
const PAPER_BLUE: [string, string, string] = ['#d0e2ff', '#a6c8ff', '#78a9ff'];
const LINE = '#8d8d8d';
const LINE_BLUE = '#0f62fe';

interface DocStackProps {
  x: number;
  y: number;
  z?: number;
  sheets?: number;
  modified?: boolean;
  /** Arkbredde (standard 2.2) */
  w?: number;
  project?: Project;
}

/** Stabel med ark; øverste ark har tekstlinjer (blå hvis «modifisert») */
export function DocStack({ x, y, z = 0, sheets = 4, modified = false, w = 2.2, project = heroIso }: DocStackProps) {
  const d = 3;
  const h = 0.14;
  const out = [];
  for (let i = 0; i < sheets; i++) {
    const top = i === sheets - 1;
    out.push(
      <Box
        key={i}
        x={x}
        y={y}
        z={z + i * (h + 0.04)}
        w={w}
        d={d}
        h={h}
        colors={top && modified ? PAPER_BLUE : PAPER}
        project={project}
      />,
    );
  }
  // Tekstlinjer på øverste flate
  const zt = z + (sheets - 1) * (h + 0.04) + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const lines = [0.2, 0.34, 0.48, 0.62, 0.76].map((v, i) => (
    <polygon
      key={`l${i}`}
      points={`${p(0.15, v)} ${p(i % 2 ? 0.7 : 0.85, v)} ${p(i % 2 ? 0.7 : 0.85, v + 0.05)} ${p(0.15, v + 0.05)}`}
      fill={modified ? LINE_BLUE : LINE}
      opacity={modified ? 0.85 : 0.7}
    />
  ));
  return (
    <g>
      {out}
      {lines}
    </g>
  );
}

interface ChipProps {
  x: number;
  y: number;
  z?: number;
  project?: Project;
}

/** ECU-brikke: mørkt hus, blå kjerne og ben på alle fire sider */
export function Chip({ x, y, z = 0, project = heroIso }: ChipProps) {
  const body: [string, string, string] = ['#525252', '#393939', '#262626'];
  const pin: [string, string, string] = ['#c6c6c6', '#a8a8a8', '#8d8d8d'];
  const die: [string, string, string] = ['#4589ff', '#0f62fe', '#0043ce'];
  const pins = [];
  for (let i = 0; i < 4; i++) {
    const o = 0.35 + i * 0.5;
    pins.push(
      <Box key={`a${i}`} x={x - 0.3} y={y + o} z={z + 0.1} w={0.3} d={0.2} h={0.12} colors={pin} project={project} />,
      <Box key={`b${i}`} x={x + o} y={y - 0.3} z={z + 0.1} w={0.2} d={0.3} h={0.12} colors={pin} project={project} />,
    );
  }
  const pinsFront = [];
  for (let i = 0; i < 4; i++) {
    const o = 0.35 + i * 0.5;
    pinsFront.push(
      <Box key={`c${i}`} x={x + 2.4} y={y + o} z={z + 0.1} w={0.3} d={0.2} h={0.12} colors={pin} project={project} />,
      <Box key={`d${i}`} x={x + o} y={y + 2.4} z={z + 0.1} w={0.2} d={0.3} h={0.12} colors={pin} project={project} />,
    );
  }
  return (
    <g>
      {pins}
      <Box x={x} y={y} z={z} w={2.4} d={2.4} h={0.5} colors={body} project={project} />
      <Box x={x + 0.6} y={y + 0.6} z={z + 0.5} w={1.2} d={1.2} h={0.12} colors={die} project={project} />
      {pinsFront}
    </g>
  );
}
